import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { updateUserActivity } from '../lib/supabase/users';

const AuthContext = createContext({});

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [company, setCompany] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isFetchingRef = useRef(false);
    const isInitializedRef = useRef(false);

    // Fetch user profile and company data
    const fetchUserProfile = async (userId) => {
        if (isFetchingRef.current) {
            return null;
        }

        isFetchingRef.current = true;

        try {
            const { data: profile, error: profileError } = await supabase
                .from('users')
                .select(`
          *,
          companies (
            id,
            name,
            slug,
            logo_url,
            primary_color,
            secondary_color,
            timezone,
            settings,
            subscription_plan,
            subscription_status,
            is_active,
            enterprise_logo_url,
            enterprise_icon_url,
            app_version,
            address,
            city,
            state,
            postal_code,
            website,
            phone,
            country
          )
        `)
                .eq('id', userId)
                .maybeSingle();

            if (profileError) {
                console.error('Profile fetch error:', profileError);
                setError(`Failed to fetch profile: ${profileError.message}`);
                return null;
            }

            // No profile yet (new self-registered user before sandbox setup completes)
            if (!profile) {
                setUserProfile(null);
                setCompany(null);
                return null;
            }

            setUserProfile(profile);
            setCompany(profile.companies);
            setError(null);

            // Update last active timestamp (non-blocking)
            updateUserActivity(userId).catch(err =>
                console.warn('Failed to update user activity:', err)
            );

            return profile;
        } catch (error) {
            console.error('❌ Unexpected error fetching user profile:', error);
            setError(error.message);
            return null;
        } finally {
            isFetchingRef.current = false;
        }
    };

    // Initialize auth state
    useEffect(() => {
        let mounted = true;

        const initializeAuth = async () => {
            try {

                const { data: { session }, error: sessionError } = await supabase.auth.getSession();

                if (sessionError) {
                    console.error('Session error:', sessionError);
                    throw sessionError;
                }


                if (!mounted) return;

                setSession(session);
                setUser(session?.user ?? null);

                if (session?.user) {
                    await fetchUserProfile(session.user.id);
                }

                setLoading(false);
            } catch (error) {
                console.error('Auth initialization error:', error);
                setError(error.message);
            } finally {
                if (mounted) {
                    isInitializedRef.current = true;
                    setLoading(false);
                }
            }
        };

        initializeAuth();

        // Listen for auth changes - NO ASYNC operations in callback
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, session) => {

                if (!mounted) return;

                // 🆕 Skip state updates if we're still in initial loading
                if (!isInitializedRef.current) {
                    return;
                }

                setSession(session);
                setUser(session?.user ?? null);

                // Use setTimeout to defer async operations outside the callback
                setTimeout(() => {
                    if (!mounted) return;

                    if (session?.user) {
                        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
                            fetchUserProfile(session.user.id);
                        } else if (event === 'TOKEN_REFRESHED') {
                            // console.log('Token refreshed - keeping existing profile');
                            // Don't refetch on token refresh, profile should already exist
                        } else if (event === 'INITIAL_SESSION') {
                            // console.log('Initial session - profile already fetched in init');
                        }
                    } else if (event === 'SIGNED_OUT') {
                        setUserProfile(null);
                        setCompany(null);
                    }
                }, 0);
            }
        );

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, []);

    // Update favicon when company changes
    useEffect(() => {
        if (company?.subscription_plan === "enterprise") {

            // 1. Change favicon if exists
            if (company?.enterprise_icon_url?.length > 0) {
                let link = document.querySelector("link[rel~='icon']");

                if (!link) {
                    link = document.createElement('link');
                    link.rel = 'icon';
                    document.getElementsByTagName('head')[0].appendChild(link);
                }

                link.type = 'image/x-icon';
                link.href = company.enterprise_icon_url;
            }

            // 2. Change page title
            if (company?.name) {
                document.title = `${company.name} - Notify`;
                // or: `${company.name} | YourAppName`
            }
        }
    }, [company]);

    // Sign in with email and password
    const signIn = async (email, password) => {
        try {
            setError(null);

            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) {
                console.error('Sign in error:', error);
                throw error;
            }

            // Profile will be fetched by onAuthStateChange SIGNED_IN event
            return { data, error: null };
        } catch (error) {
            console.error('❌ Sign in failed:', error);
            setError(error.message);
            return { data: null, error };
        }
    };

    // Sign out
    const signOut = async () => {
        try {
            // Clear state immediately first
            setUser(null);
            setUserProfile(null);
            setCompany(null);
            setSession(null);
            setError(null);

            // Then sign out from Supabase with scope: 'local' to ensure proper cleanup
            const { error } = await supabase.auth.signOut({ scope: 'local' });
            if (error) throw error;

            // Clear any localStorage items that might be cached
            // Supabase stores session in localStorage by default
            try {
                // Force clear Supabase auth storage
                localStorage.removeItem('supabase.auth.token');
                // Clear all keys that start with 'sb-' (Supabase prefix)
                Object.keys(localStorage).forEach(key => {
                    if (key.startsWith('sb-')) {
                        localStorage.removeItem(key);
                    }
                });
            } catch (e) {
                console.warn('Could not clear localStorage:', e);
            }

            return { error: null };
        } catch (error) {
            console.error('❌ Sign out error:', error);
            return { error };
        }
    };

    // Refresh user profile
    const refreshProfile = async () => {
        if (user) {
            return await fetchUserProfile(user.id);
        }
        return null;
    };

    const value = {
        user,
        userProfile,
        company,
        session,
        loading,
        error,
        signIn,
        signOut,
        refreshProfile,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};